import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Icon } from '../../../ui/Icon';

interface Props {
  /** Moment names already on the day, so they aren't offered again. */
  taken: string[];
  onCreate: (title: string) => Promise<void>;
}

/**
 * Splits a day into moments ("Beach", "Dinner"). ADR-0017: a moment is an
 * OutfitGroup with a free title — the presets are only a shortcut to typing
 * one, never a fixed set of slots.
 */
export default function OutfitMomentPicker({ taken, onCreate }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const takenLower = new Set(taken.map(title => title.trim().toLowerCase()));
  const presets = (t('packing.outfits.groupPresets', { returnObjects: true }) as string[])
    .filter(name => !takenLower.has(name.toLowerCase()));

  async function create(title: string) {
    const trimmed = title.trim();
    if (!trimmed) return;
    setDraft('');
    setOpen(false);
    await onCreate(trimmed);
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 -ml-1.5 text-xs text-slate-400 hover:text-teal-700 hover:bg-teal-50 dark:hover:text-teal-300 dark:hover:bg-teal-900/25 transition-colors cursor-pointer"
      >
        <Icon as={Plus} size={12} />
        {t('packing.outfits.addGroup')}
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-3">
      <div className="flex items-center mb-2">
        <p className="flex-1 text-xs font-medium text-slate-600 dark:text-slate-300">{t('packing.outfits.groupPrompt')}</p>
        <button
          onClick={() => setOpen(false)}
          className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
          aria-label={t('common.close')}
        >
          <Icon as={X} size={12} />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {presets.map(name => (
          <button
            key={name}
            onClick={() => create(name)}
            className="text-xs px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-900/25 dark:hover:text-teal-300 transition-colors cursor-pointer"
          >
            {name}
          </button>
        ))}
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') create(draft);
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder={t('packing.outfits.groupPlaceholder')}
          aria-label={t('packing.outfits.groupName')}
          className="min-w-[8rem] flex-1 bg-transparent border-b border-slate-200 dark:border-slate-700 focus:border-teal-400 text-xs py-1 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none"
        />
      </div>
    </div>
  );
}
